import { AfterViewInit, Component, Inject, Input, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { range } from 'lodash';
import moment from 'moment-timezone';
import { Observable, Subscription, combineLatest, EMPTY } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';


import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { RSSFeedCreateDialogComponent } from '../RSSFeedCreateDialog/rss-feed-create-dialog.component';
import { TableColumnModel } from '../../../../Common/Forms/models/table-column.model';
import { TableColumnInputEnum } from '../../../../Common/Forms/enums/table-column-input-type.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { CommonRowFields } from '../../../../Common/Tags/components/ManageTagsDialog/manage-tags-dialog.component';
import { RSSFeedService } from '../../services/rss-feed.service';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { RSSFeed } from '../../models/rss-feed.model';
import { BriefingListItemModel } from '../../../../Common/MonitorShared/model/briefing-list-item.model';
import { LinkModel } from '../../../../Common/Forms/models/link.model';
import { BriefingTypeEnum } from '../../../Briefings/enums/briefing-type.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

interface FeedDataRow extends CommonRowFields {
    id: number;
    originalData: RSSFeed;
    description: string;
    sources?: LinkModel[];
    maxItems?: DropdownOptionModel;
    url: string;
    enabled: boolean;
}
@Component({
    selector: 'agpr-rss-feed-manage-dialog',
    templateUrl: './rss-feed-manage-dialog.component.html',
    styleUrls: ['./rss-feed-manage-dialog.component.scss'],
    standalone: false
})
export class RSSFeedManageDialogComponent extends AbstractDialogComponent implements AfterViewInit, OnDestroy {

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    @ViewChild('itemsLimitCell') itemsLimitCellRef: TemplateRef<any>;
    @ViewChild('customActionsCell') customActionsCellRef: TemplateRef<any>;
    @ViewChild('descriptionCell') descriptionCellRef: TemplateRef<any>;

    hideSlideOverlay: boolean = false;

    selectedItemsLimitOption: DropdownOptionModel;
    hasSelectedFeeds: boolean = false;
    feedToBriefingsMap: Map<number, BriefingListItemModel[]>;

    columnDefs: TableColumnModel[] = [];

    dataRows: FeedDataRow[] = [];
    allFeeds: RSSFeed[];

    private subscriptions: Subscription[] = [];

    readonly FEED_LIMIT = 3;
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly itemsLimitOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('3', 3),
        new DropdownOptionModel('5', 5),
        // final option is 100, as range() end is exclusive
        ...range(10, 105, 5).map(x => new DropdownOptionModel(x.toString(), x))
    ];
    readonly nameColumn: TableColumnModel = {
        heading: 'RSSFeeds.name',
        field: 'name',
        tooltipField: 'name',
        fieldMaxLength: 64,
        sortable: true,
        sortType: 'text',
        type: TableColumnInputEnum.text,
        cellStyleClass: 'agpr-rssFeedManageDialog-feedName',
        selectable: true,
        selectField: 'selected',
        width: 170
    };
    readonly descriptionColumn: TableColumnModel = {
        heading: 'RSSFeeds.description',
        field: 'description',
        width: 230,
        fieldMaxLength: 200,
        type: TableColumnInputEnum.textarea
    };
    readonly itemsLimitColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'RSSFeeds.maxItems',
        field: 'maxItems',
        width: 140,
        cellStyleClass: 'agpr-rssFeedManageDialog-customCellStyle'
    };
    readonly sourcesColumn: TableColumnModel = {
        type: TableColumnInputEnum.linkList,
        heading: 'RSSFeeds.sources',
        field: 'sources',
        width: 200,
        message: 'RSSFeeds.numberOfSources',
        cellStyleClass: 'agpr-rssFeedManageDialog-customCellStyle',
    };
    readonly customActionsColumn: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'action',
        cellStyleClass: 'agpr-rssFeedManageDialog-customActions',
        field: 'enabled'
    };

    constructor(
        public coreDialogService: CoreDialogService,
        private rssFeedService: RSSFeedService,
        private usersService: UsersService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        super(coreDialogService, 'RSSFeedManage');

        this.subscriptions.push(
            combineLatest([
                this.rssFeedService.all$,
                this.rssFeedService.feedToBriefingsMap$,
            ]).subscribe(([feeds, feedToBriefings]) => {
                if (!feeds) {
                    this.allFeeds = [];
                    this.hasSelectedFeeds = false;
                    return;
                }
                this.allFeeds = feeds;
                this.feedToBriefingsMap = feedToBriefings;
                this.setData();
            })
        );
    }

    ngAfterViewInit() {
        requestAnimationFrame(() => {
            this.itemsLimitColumn.customTemplate = this.itemsLimitCellRef;
            this.customActionsColumn.customTemplate = this.customActionsCellRef;
            this.descriptionColumn.customTemplate = this.descriptionCellRef;
            this.columnDefs = [
                this.nameColumn,
                this.descriptionColumn,
                this.sourcesColumn,
                this.itemsLimitColumn,
                this.customActionsColumn
            ];
        });
    }

    saveFeed(feedRow: FeedDataRow) {

        const feed = feedRow.originalData;
        const patch = this.buildPatch(feedRow, feed);

        if (!patch) {

            return;
        }

        const updatedFeed = Object.assign(feed.clone(), patch);

        const update$ = patch.name
            ? this.rssFeedService.isNameDuplicated$(patch.name).pipe(
                switchMap(isDuplicate => {
                    if (isDuplicate) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'RSSFeeds.duplicateName.error.heading',
                            detail: this.localizationService.transform('RSSFeeds.duplicateName.error.message', {
                                name: feed.name
                            })
                        });

                        // revert the displayed value
                        feedRow.name = feed.name;

                        return EMPTY;
                    }

                    return this.rssFeedService.update(updatedFeed);
                })
            )
            : this.rssFeedService.update(updatedFeed);

        update$.subscribe(() => Object.assign(feed, patch));
    }

    private setData() {
        this.dataRows = this.allFeeds.map(feed => {
            const row: FeedDataRow = {
                id: feed.id,
                name: feed.name,
                description: feed.description,
                url: feed.url,
                enabled: feed.enabled,
                originalData: feed,
                createdDate: moment(feed.createdDate).format('MMMM D, YYYY'),
                lastModifiedDate: moment(feed.lastModifiedDate).format('MMMM D, YYYY'),
            };

            // set briefing link
            let sources: BriefingListItemModel[];
            let briefingLinks = [];

            if (this.feedToBriefingsMap) {
                sources = this.feedToBriefingsMap.get(feed.id);

                if (sources) {
                    briefingLinks = sources.map(briefing => {
                        let url;
                        if (briefing.type === BriefingTypeEnum.BRIEFING) {
                            url = `${this.appBaseHref}Monitoring/briefings/briefing/${briefing.id}`;
                        } else if (briefing.type === BriefingTypeEnum.ALERT) {
                            url = `${this.appBaseHref}Monitoring/alerts/alert/${briefing.id}`;
                        };
                        return {
                            label: `${briefing.name} (${briefing.type.toLowerCase()})`,
                            url: url
                        };
                    });
                    row.sources = briefingLinks;
                }
            }

            // set default selection of feed items limit
            const selectedMaxItemsOptionIndex = this.itemsLimitOptions.findIndex(option => option.value === row.originalData.maxItems);
            if (selectedMaxItemsOptionIndex) {
                row.maxItems = this.itemsLimitOptions[selectedMaxItemsOptionIndex];
            } else {
                row.maxItems = this.itemsLimitOptions[0];
            }

            return row;
        });

        this.checkForSelectedFeeds();
        this.fetchUserNames();
    }

    showCreateDialog(): void {
        if (this.dataRows.length === this.FEED_LIMIT) {
            const confirmDialogModel: ConfirmDialogModel = {
                inputs: {
                    heading: 'RSSFeeds.error.feedLimitHeader',
                    messageHtml: 'RSSFeeds.error.feedLimitMessageHtml',
                    alertType: messageSeverityEnum.error,
                    okLabel: 'RSSFeeds.error.feedLimitOkLabel',
                    showCancel: false
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.closeDialog();
                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);
        } else {
            this.coreDialogService.open(RSSFeedCreateDialogComponent);
        }
    }

    checkForSelectedFeeds() {
        this.hasSelectedFeeds = !!this.dataRows.find(row => row.selected);
    }


    deleteSelectedFeeds() {
        const selectedRows = this.dataRows.filter(row => row.selected);
        const selectedFeeds = selectedRows.map(row => row.originalData);
        this.rssFeedService.confirmBulkDelete(selectedFeeds);
    }


    deleteFeed(feedRow: FeedDataRow) {
        const feed = feedRow.originalData;
        this.rssFeedService.confirmBulkDelete([feed]);
    }

    cancel() {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    /**
     * For the info tooltip, convert user ids to usernames
     */
    private fetchUserNames() {
        const userIds = [...this.dataRows.map(x => +x.originalData.createdByUserId), ...this.dataRows.map(x => +x.originalData.lastModifiedByUserId)];
        this.subscriptions.push(
            this.usersService.getUsers(userIds).subscribe(users => {
                this.dataRows.forEach(row => {
                    const creator = users.find(x => x.id === row.originalData.createdByUserId);
                    if (creator) {
                        row.createdByUser = creator.getDisplayName();
                    }
                    const modifier = users.find(x => x.id === row.originalData.lastModifiedByUserId);
                    if (modifier) {
                        row.lastModifiedByUser = modifier.getDisplayName();
                    }
                });
            })
        );
    }



    private buildPatch(feedRow: FeedDataRow, feed: RSSFeed): Partial<RSSFeed> | null {
        const patch: Partial<RSSFeed> = {};

        if (feedRow.name !== feed.name) {
            patch.name = feedRow.name;
        }

        if (feedRow.description !== feed.description) {
            patch.description = feedRow.description;
        }

        if (feedRow.enabled !== feed.enabled) {
            patch.enabled = feedRow.enabled;
        }

        if (feedRow.maxItems.value !== feed.maxItems) {
            patch.maxItems = feedRow.maxItems.value;
        }

        return Object.keys(patch).length ? patch : null;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
